import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Alert, Button, Spinner } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  AddTicketNote,
  DismissSupportTicket,
  EscalateSupportTicket,
  GetResponseSnippets,
  GetSupportTicket,
  GetSupportTicketAudit,
  GetTicketNotes,
  GrabSupportTicket,
  PortalAuditEntry,
  ReplyToSupportTicket,
  RerouteSupportTicket,
  ResponseSnippet,
  ResponseSnippetsResponse,
  SetSupportTicketStatus,
  SupportTicketDetailResponse,
  TicketInternalNote,
  UndismissSupportTicket,
} from '../../services/dtos';
import {
  ATTACHMENT_ACCEPT,
  ComposerField,
  ESCALATION_TARGETS,
  LanguageWarning,
  MisuseDisclaimer,
  MemberStatusBadge,
  TICKET_QUEUES,
  TicketCategoryBadge,
  TicketConfidentialBadge,
  TicketEscalatedBadge,
  TicketStatusBadge,
  TicketUrgentBadge,
  attachmentPrecheck,
  attachmentUrl,
  categoryByValue,
  checkLanguage,
  fmtBytes,
  fmtDate,
  fmtDateTime,
  roleLabel,
  uploadTicketAttachment,
} from './supportShared';
import { LanguageFilterHit } from '../../services/dtos';

const SupportTicketDetailPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const ticketId = parseInt(id || '0', 10);

  const [detail, setDetail] = useState<SupportTicketDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(
    // the composer routes here after submit; a failed evidence upload
    // surfaces on the ticket it belongs to, not on the (gone) composer
    (location.state as any)?.uploadError || null
  );
  const [busy, setBusy] = useState(false);

  const [reply, setReply] = useState('');
  // T7: spans flagged by the pre-flight language check (inline reword UX)
  const [langHits, setLangHits] = useState<LanguageFilterHit[]>([]);
  // T6: snippets dropped into the current reply — audited server-side as
  // body-at-insert vs text-as-sent
  const [usedSnippetIds, setUsedSnippetIds] = useState<number[]>([]);

  useEffect(() => {
    if ((location.state as any)?.uploadError)
      window.history.replaceState({}, ''); // don't re-toast on refresh
  }, []); // once, on mount

  const load = useCallback(() => {
    const req = new GetSupportTicket();
    req.id = ticketId;
    return apiServices.client
      .get(req)
      .then((res: SupportTicketDetailResponse) => {
        setDetail(res);
        setLoading(false);
      })
      .catch((e: any) => {
        setLoadError(
          e?.responseStatus?.errorCode === 'Forbidden' || e?.status === 403
            ? "You don't have access to this ticket."
            : e?.responseStatus?.message || 'Failed to load this ticket.'
        );
        setLoading(false);
      });
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  // Every staff action returns the refreshed detail — one handler for all.
  const runAction = async (fn: () => Promise<SupportTicketDetailResponse>) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      setDetail(await fn());
    } catch (e: any) {
      setActionError(e?.responseStatus?.message || 'That action failed.');
    } finally {
      setBusy(false);
    }
  };

  const replyFields = (): ComposerField[] => [
    { key: 'reply', label: 'Your reply', text: reply },
  ];

  const sendReply = async () => {
    // T7 inline reword: flag banned language before the reply is attempted.
    // Advisory — the server re-scans inside reply either way.
    const hits = await checkLanguage(replyFields());
    setLangHits(hits);
    if (hits.length > 0) return;
    runAction(async () => {
      const req = new ReplyToSupportTicket();
      req.id = ticketId;
      req.body = reply.trim();
      if (usedSnippetIds.length > 0) req.usedSnippetIds = usedSnippetIds;
      const res = await apiServices.client.post(req);
      setReply('');
      setUsedSnippetIds([]);
      return res;
    });
  };

  const insertSnippet = (s: ResponseSnippet) => {
    setReply((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${s.body}` : s.body || ''));
    setUsedSnippetIds((prev) => (prev.includes(s.id) ? prev : [...prev, s.id]));
  };

  if (loading)
    return (
      <div className="flex justify-center pt-10">
        <Spinner color="info" />
      </div>
    );

  if (loadError || !detail?.ticket)
    return (
      <div className="mx-auto w-full max-w-2xl dark:text-white">
        <Alert color="failure" icon={HiInformationCircle}>
          {loadError || 'Ticket not found.'}
        </Alert>
        <div className="mt-4">
          <Link to="/help" className="text-sm text-indigo-600 dark:text-indigo-400">
            ← Back to my requests
          </Link>
        </div>
      </div>
    );

  const t = detail.ticket;
  const dismissed = t.status === 'Dismissed';

  return (
    <div className="mx-auto w-full flex max-w-3xl flex-col gap-6 dark:text-white">
      <div>
        <Link
          to={detail.viewerIsStaff ? '/help/queue' : '/help'}
          className="text-sm text-indigo-600 dark:text-indigo-400"
        >
          ← Back
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3">
          <h2 className="text-2xl font-bold break-words">{t.subject}</h2>
          {/* T5: members see WHERE their request stands ("Received", "With
              dispatch", "Escalated to the Business Representative",
              "Answered"); staff keep the workflow status */}
          {detail.viewerIsStaff ? (
            <TicketStatusBadge status={t.status} />
          ) : (
            <MemberStatusBadge ticket={t} />
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          <span>#{t.id}</span>
          <TicketCategoryBadge category={t.category} />
          <TicketUrgentBadge isUrgent={t.isUrgent} />
          <TicketConfidentialBadge isConfidential={t.isConfidential} />
          {detail.viewerIsStaff && <TicketEscalatedBadge toRole={t.escalatedToRole} />}
          <span>Opened {fmtDateTime(t.createdAtUtc)}</span>
          {detail.viewerIsStaff && (
            <span>
              From{' '}
              {/* S1: the member's running record */}
              <Link
                to={`/help/member/${t.memberUniqueMemberId}`}
                className="text-indigo-600 hover:underline dark:text-indigo-400"
              >
                {t.memberName}
              </Link>
            </span>
          )}
          {t.ownerName && <span>Handled by {t.ownerName}</span>}
        </div>
        {detail.viewerIsStaff && t.escalatedToRole && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Escalated to the {roleLabel(t.escalatedToRole)} by {t.escalatedByName}{' '}
            {fmtDateTime(t.escalatedAtUtc)}
          </div>
        )}
        {t.showSnapshot ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Related show: {[t.showSnapshot, t.siteSnapshot].filter(Boolean).join(' — ')}
            {t.startingDateSnapshot ? ` (${fmtDate(t.startingDateSnapshot)})` : ''}
          </div>
        ) : t.relatedShowFreeText ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Related show/employer (as typed): {t.relatedShowFreeText}
          </div>
        ) : null}
      </div>

      <FormDetailsPanel ticket={t} />

      {actionError && (
        <Alert color="failure" icon={HiInformationCircle}>
          {actionError}
        </Alert>
      )}

      {detail.viewerIsStaff && (
        <StaffActions detail={detail} busy={busy} runAction={runAction} />
      )}

      {/* thread */}
      <div className="flex flex-col gap-3">
        {detail.messages?.map((m) => (
          <div
            key={m.id}
            className={`rounded-lg border p-3 ${
              m.authorIsStaff
                ? 'border-indigo-200 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
            }`}
          >
            <div className="mb-1 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                {m.authorName}
                {m.authorIsStaff ? ' · Union staff' : ''}
              </span>
              <span>{fmtDateTime(m.createdAtUtc)}</span>
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
              {m.body}
            </div>
          </div>
        ))}
      </div>

      <AttachmentsPanel
        detail={detail}
        // T7: a ticket ban hides the upload affordance the same way a
        // dismissal does (the server 403s the upload regardless)
        dismissed={dismissed || !!detail.viewerTicketBanned}
        onChanged={load}
        onError={setActionError}
      />

      {/* reply composer — T7: an active ticket ban replaces it with the
          A-09 message (reading the ticket stays open) */}
      {detail.viewerTicketBanned ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-gray-900 p-4 text-sm text-red-800 dark:text-red-300">
          {detail.viewerBannedMessage}
        </div>
      ) : dismissed ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400">
          This ticket has been closed and no longer accepts replies.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <textarea
            rows={4}
            className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Write a reply…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          {/* T6: one-click canned answers, editable after insert */}
          {detail.viewerIsStaff && <SnippetPicker onInsert={insertSnippet} />}
          <MisuseDisclaimer />
          <LanguageWarning fields={replyFields()} hits={langHits} />
          <div>
            <Button onClick={sendReply} disabled={busy || !reply.trim()}>
              {busy ? <Spinner size="sm" /> : 'Send Reply'}
            </Button>
          </div>
        </div>
      )}

      {detail.viewerIsStaff && (
        <>
          <NotesPanel ticketId={ticketId} />
          <AuditPanel ticketId={ticketId} refreshKey={detail} />
        </>
      )}
    </div>
  );
};

// ── T4: the category form's structured fields (Forms A/B/C) ────────────────

const fmtMoney = (v?: number) =>
  v == null ? null : `$${v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

function FormDetailsPanel({ ticket: t }: { ticket: any }) {
  const rows: Array<[string, string | null | undefined]> = [
    ['Date worked', t.workDate ? fmtDate(t.workDate) : null],
    ['Position', t.position],
    ['Pay expected', fmtMoney(t.expectedPay)],
    ['Pay received', fmtMoney(t.actualPay)],
    ['Hours worked', t.hoursWorked != null ? String(t.hoursWorked) : null],
    ['Rate applied', t.rateApplied],
    ['Call type', t.callType],
    ['Date of the call', t.callDate ? fmtDate(t.callDate) : null],
  ].filter(([, v]) => v) as Array<[string, string]>;

  const longRows: Array<[string, string]> = [
    ['OT / penalties possibly missed', t.otPenaltyMissed],
    ['Their understanding of the contract', t.contractUnderstanding],
  ].filter(([, v]) => v) as Array<[string, string]>;

  if (rows.length === 0 && longRows.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
        Request details
      </h3>
      {rows.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
              <dd className="text-right text-gray-900 dark:text-gray-100 break-words">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {longRows.map(([label, value]) => (
        <div key={label} className="mt-2 text-sm">
          <div className="text-gray-500 dark:text-gray-400">{label}</div>
          <div className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── attachments (T3): evidence files, visible to everyone who can see the
// ticket; downloads re-check access server-side on every request ────────────

function AttachmentsPanel({
  detail,
  dismissed,
  onChanged,
  onError,
}: {
  detail: SupportTicketDetailResponse;
  dismissed: boolean;
  onChanged: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const attachments = detail.attachments || [];

  // T4: evidence exists on A/B/F/G only (the server 400s the rest);
  // unknown/legacy categories keep the T3 behavior
  const allowsEvidence =
    categoryByValue(detail.ticket!.category)?.evidence !== false;

  // nothing to show and nothing to add — stay out of the way
  if (attachments.length === 0 && (dismissed || !allowsEvidence)) return null;

  const pick = () => fileInput.current?.click();

  const upload = async (file: File | undefined) => {
    if (!file || uploading) return;
    onError(null);
    const problem = attachmentPrecheck(file);
    if (problem) {
      onError(problem);
      return;
    }
    setUploading(true);
    try {
      await uploadTicketAttachment(detail.ticket!.id, file);
      await onChanged();
    } catch (e: any) {
      onError(e?.message || 'The file upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Files
        </h3>
        {!dismissed && allowsEvidence && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button size="xs" color="gray" onClick={pick} disabled={uploading}>
              {uploading ? <Spinner size="sm" /> : 'Add File'}
            </Button>
          </>
        )}
      </div>

      {attachments.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No files yet — pdf, jpg, png, heic, xlsx, or csv, up to 15 MB.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm"
            >
              <a
                href={attachmentUrl(a.id)}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-indigo-600 dark:text-indigo-400 break-all"
              >
                📎 {a.fileName}
              </a>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {fmtBytes(a.sizeBytes)} · {a.uploadedByName}
                {a.uploadedByIsStaff ? ' (staff)' : ''} ·{' '}
                {fmtDateTime(a.createdAtUtc)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── staff: grab / escalate / reroute / status / dismiss ─────────────────────

function StaffActions({
  detail,
  busy,
  runAction,
}: {
  detail: SupportTicketDetailResponse;
  busy: boolean;
  runAction: (fn: () => Promise<SupportTicketDetailResponse>) => Promise<void>;
}) {
  const t = detail.ticket!;
  const dismissed = t.status === 'Dismissed';

  // T5: which inline panel is open under the staff bar
  const [panel, setPanel] = useState<'escalate' | 'reroute' | null>(null);
  const [panelTarget, setPanelTarget] = useState('');
  const [panelNote, setPanelNote] = useState('');

  const post = (req: any) =>
    runAction(async () => {
      const res: SupportTicketDetailResponse = await apiServices.client.post(req);
      setPanel(null);
      setPanelTarget('');
      setPanelNote('');
      return res;
    });

  const grab = () => {
    const req = new GrabSupportTicket();
    req.id = t.id;
    post(req);
  };
  const setStatus = (status: string) => {
    const req = new SetSupportTicketStatus();
    req.id = t.id;
    req.status = status;
    post(req);
  };
  const dismiss = () => {
    const reason = window.prompt(
      'Dismiss this ticket? The record and audit trail are kept.\n\nReason (recorded in the audit log):'
    );
    if (reason === null) return;
    const req = new DismissSupportTicket();
    req.id = t.id;
    req.reason = reason;
    post(req);
  };
  const undismiss = () => {
    const req = new UndismissSupportTicket();
    req.id = t.id;
    post(req);
  };
  const escalate = () => {
    const req = new EscalateSupportTicket();
    req.id = t.id;
    req.toRole = panelTarget;
    req.note = panelNote.trim() || undefined;
    post(req);
  };
  const returnToQueue = () => {
    const req = new EscalateSupportTicket();
    req.id = t.id;
    req.toRole = '';
    post(req);
  };
  const reroute = () => {
    const req = new RerouteSupportTicket();
    req.id = t.id;
    req.queue = panelTarget;
    req.note = panelNote.trim() || undefined;
    post(req);
  };

  const openPanel = (which: 'escalate' | 'reroute') => {
    setPanel(panel === which ? null : which);
    setPanelTarget('');
    setPanelNote('');
  };

  const selectCls =
    'block p-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white';

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          Staff
        </span>
        {!dismissed && (
          <>
            <Button size="xs" onClick={grab} disabled={busy}>
              {t.ownerName ? 'Take Over' : 'Grab'}
            </Button>
            <Button size="xs" color="purple" onClick={() => openPanel('escalate')} disabled={busy}>
              Escalate…
            </Button>
            {t.escalatedToRole && (
              <Button size="xs" color="purple" onClick={returnToQueue} disabled={busy}>
                Return to Queue
              </Button>
            )}
            <Button size="xs" color="gray" onClick={() => openPanel('reroute')} disabled={busy}>
              Move to Queue…
            </Button>
            {t.status !== 'InProgress' && (
              <Button size="xs" color="warning" onClick={() => setStatus('InProgress')} disabled={busy}>
                Mark In Progress
              </Button>
            )}
            {t.status !== 'Resolved' && (
              <Button size="xs" color="success" onClick={() => setStatus('Resolved')} disabled={busy}>
                Mark Resolved
              </Button>
            )}
            <Button size="xs" color="gray" onClick={dismiss} disabled={busy}>
              Dismiss
            </Button>
          </>
        )}
        {dismissed && (
          <Button size="xs" color="warning" onClick={undismiss} disabled={busy}>
            Undismiss
          </Button>
        )}
      </div>

      {panel === 'escalate' && (
        <div className="flex flex-col gap-2 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Send this up to the right person. The full history travels with the
            ticket; your note lands in the audit trail.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={selectCls}
              value={panelTarget}
              onChange={(e) => setPanelTarget(e.target.value)}
            >
              <option value="">Escalate to…</option>
              {ESCALATION_TARGETS.filter((r) => r !== t.escalatedToRole).map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <input
              className={`${selectCls} flex-1 min-w-[200px]`}
              placeholder="What's been tried so far? (optional)"
              value={panelNote}
              onChange={(e) => setPanelNote(e.target.value)}
            />
            <Button size="xs" onClick={escalate} disabled={busy || !panelTarget}>
              Escalate
            </Button>
          </div>
        </div>
      )}

      {panel === 'reroute' && (
        <div className="flex flex-col gap-2 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Move a miscategorized request to the right queue. Ownership and any
            escalation reset.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={selectCls}
              value={panelTarget}
              onChange={(e) => setPanelTarget(e.target.value)}
            >
              <option value="">Move to…</option>
              {TICKET_QUEUES.filter((q) => q.name !== t.queue).map((q) => (
                <option key={q.name} value={q.name}>
                  {q.label}
                </option>
              ))}
            </select>
            <input
              className={`${selectCls} flex-1 min-w-[200px]`}
              placeholder="Why? (optional, recorded in the audit log)"
              value={panelNote}
              onChange={(e) => setPanelNote(e.target.value)}
            />
            <Button size="xs" onClick={reroute} disabled={busy || !panelTarget}>
              Move
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── T6: the snippet picker under the staff reply composer ──────────────────

function SnippetPicker({ onInsert }: { onInsert: (s: ResponseSnippet) => void }) {
  const [open, setOpen] = useState(false);
  const [snippets, setSnippets] = useState<ResponseSnippet[] | null>(null);
  const [error, setError] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && snippets === null)
      apiServices.client
        .get(new GetResponseSnippets())
        .then((res: ResponseSnippetsResponse) =>
          setSnippets((res.snippets || []).filter((s) => !s.archivedAtUtc))
        )
        .catch(() => setError(true));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
        >
          {open ? 'Hide saved answers ▴' : 'Insert a saved answer ▾'}
        </button>
        <Link
          to="/help/snippets"
          className="text-xs text-gray-500 dark:text-gray-400 underline"
        >
          manage
        </Link>
      </div>
      {open && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 max-h-56 overflow-y-auto">
          {error && (
            <div className="p-3 text-sm text-red-700 dark:text-red-300">
              Failed to load saved answers.
            </div>
          )}
          {snippets !== null && snippets.length === 0 && !error && (
            <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
              No saved answers yet — add some under “manage”.
            </div>
          )}
          {snippets === null && !error && (
            <div className="p-3">
              <Spinner size="sm" />
            </div>
          )}
          {snippets?.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onInsert(s)}
              className="block w-full border-b border-gray-100 dark:border-gray-700 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {s.title}
              </div>
              <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                {s.body}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── staff: internal notes (never visible to members — separate table+endpoint) ──

function NotesPanel({ ticketId }: { ticketId: number }) {
  const [notes, setNotes] = useState<TicketInternalNote[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const req = new GetTicketNotes();
    req.id = ticketId;
    apiServices.client
      .get(req)
      .then(setNotes)
      .catch(() => setError('Failed to load internal notes'));
  }, [ticketId]);

  const addNote = async () => {
    if (busy || !text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const req = new AddTicketNote();
      req.id = ticketId;
      req.body = text.trim();
      setNotes(await apiServices.client.post(req));
      setText('');
    } catch (e: any) {
      setError(e?.responseStatus?.message || 'Failed to add the note');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-gray-900 p-4 flex flex-col gap-3">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white">
          Internal notes
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Staff only — members never see these. Notes are permanent: they
          cannot be edited or deleted, and every note is recorded in the audit
          trail.
        </p>
      </div>

      {error && (
        <Alert color="failure" icon={HiInformationCircle}>
          {error}
        </Alert>
      )}

      {notes.length === 0 && !error && (
        <div className="text-sm text-gray-500 dark:text-gray-400">No notes yet.</div>
      )}
      {notes.map((n) => (
        <div
          key={n.id}
          className="rounded border border-amber-200 dark:border-amber-800 bg-white dark:bg-gray-800 p-3"
        >
          <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {n.authorName}
            </span>
            <span>{fmtDateTime(n.createdAtUtc)}</span>
          </div>
          <div className="whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
            {n.body}
          </div>
        </div>
      ))}

      <textarea
        rows={3}
        className="block p-2.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
        placeholder="Add an internal note (permanent)…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <MisuseDisclaimer />
      <div>
        <Button size="sm" color="warning" onClick={addNote} disabled={busy || !text.trim()}>
          {busy ? <Spinner size="sm" /> : 'Add Note'}
        </Button>
      </div>
    </div>
  );
}

// ── staff: audit timeline ───────────────────────────────────────────────────

function AuditPanel({ ticketId, refreshKey }: { ticketId: number; refreshKey: unknown }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<PortalAuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const req = new GetSupportTicketAudit();
    req.id = ticketId;
    apiServices.client
      .get(req)
      .then(setEntries)
      .catch(() => setError('Failed to load the audit trail'));
  }, [ticketId]);

  // refetch on open, and keep fresh as staff actions change the ticket
  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <button
        className="w-full text-left font-semibold text-gray-900 dark:text-white"
        onClick={() => setOpen(!open)}
      >
        Audit trail {open ? '▾' : '▸'}
      </button>

      {open && (
        <div className="mt-3 flex flex-col gap-2">
          {error && (
            <Alert color="failure" icon={HiInformationCircle}>
              {error}
            </Alert>
          )}
          {!entries && !error && <Spinner size="sm" />}
          {entries?.map((e) => (
            <div
              key={String(e.id)}
              className="border-l-2 border-gray-200 dark:border-gray-700 pl-3 text-xs text-gray-600 dark:text-gray-300"
            >
              <div className="flex flex-wrap gap-x-2 text-gray-500 dark:text-gray-400">
                <span>{fmtDateTime(e.atUtc)}</span>
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                  {e.actorName || e.actorAuthId || 'system'}
                </span>
                <span className="font-mono">{e.action}</span>
              </div>
              {e.fieldName && (
                <div className="font-mono break-words">
                  {e.fieldName}: {fmtAuditVal(e.beforeJson)} → {fmtAuditVal(e.afterJson)}
                </div>
              )}
              {e.note && <div className="italic">{e.note}</div>}
            </div>
          ))}
          {entries?.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">No entries.</div>
          )}
        </div>
      )}
    </div>
  );
}

function fmtAuditVal(json?: string): string {
  if (json === undefined || json === null || json === '') return '∅';
  try {
    const v = JSON.parse(json);
    if (v === null) return '∅';
    if (typeof v === 'string' && /\/Date\(/.test(v)) return fmtDateTime(v);
    return String(v);
  } catch {
    return json;
  }
}

export default SupportTicketDetailPage;
